<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="500" persistent>
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>{{ $t('equipment.qrcode.downloadTitle') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-container>
          <v-alert type="info" dense class="mb-4">
            {{ $t('equipment.qrcode.downloadDescription') }}
          </v-alert>
          <v-form ref="downloadQRForm" v-model="formValidado">
            <v-text-field
              v-model.number="qrCodeQuantity"
              :label="$t('equipment.qrcode.quantity')"
              type="number"
              min="1"
              :rules="qrCodeQuantityRules()"
              required
            ></v-text-field>
            <v-text-field
              v-model.number="qrCodeColumns"
              :label="$t('equipment.qrcode.columns')"
              type="number"
              min="1"
              max="8"
              :rules="qrCodeColumnsRules()"
              :hint="$t('equipment.qrcode.columnsHint')"
              persistent-hint
            ></v-text-field>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="close">
          {{ $t('button.cancel') }}
        </v-btn>
        <v-btn color="primary" @click="downloadQRCodes" :disabled="!formValidado || downloading" :loading="downloading">
          {{ $t('equipment.qrcode.download') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';

export default {
  name: 'DownloadQRDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    token: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      formValidado: false,
      qrCodeQuantity: 1,
      qrCodeColumns: 3,
      downloading: false,
    };
  },
  watch: {
    value(visible) {
      if (visible) {
        this.qrCodeQuantity = 1;
        this.qrCodeColumns = 3;
        this.$nextTick(() => {
          if (this.$refs.downloadQRForm) {
            this.$refs.downloadQRForm.resetValidation();
          }
        });
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
      this.qrCodeQuantity = 1;
      this.qrCodeColumns = 3;
      if (this.$refs.downloadQRForm) {
        this.$refs.downloadQRForm.resetValidation();
      }
    },
    qrCodeQuantityRules() {
      return [
        (v) => !!v || this.$t('equipment.qrcode.quantityRequired'),
        (v) => (v && v > 0) || this.$t('equipment.qrcode.quantityMin'),
        (v) => (v && Number.isInteger(v)) || this.$t('equipment.qrcode.quantityInteger')];
    },
    qrCodeColumnsRules() {
      return [
        (v) =>
          v == null ||
          v === '' ||
          (Number.isInteger(v) && v >= 1 && v <= 8) ||
          this.$t('equipment.qrcode.columnsMin')];
    },
    downloadQRCodes() {
      this.$refs.downloadQRForm.validate();
      if (!this.formValidado) {
        return;
      }

      if (!this.qrCodeQuantity || this.qrCodeQuantity < 1) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('equipment.qrcode.invalidQuantity'),
          tipo: 'error',
        });
        return;
      }

      this.downloading = true;

      apiController.request(apiController.endpoints.downloadQrcodesequipamentos, {
        query: {
          quantity: this.qrCodeQuantity,
          format: 'sheet',
          columns: this.qrCodeColumns || 3,
        },
        config: { responseType: 'blob' },
        silent: true,
        onSuccess: (response) => {
          const blob = new Blob([response.data], { type: 'image/png' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `qrcodes_equipamentos_stickers_${Date.now()}.png`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          this.downloading = false;
          this.close();

          EventBus.$emit('show-alert', {
            mensagem: this.$t('equipment.qrcode.downloadSuccess'),
            tipo: 'success',
          });
        },
        onError: (e) => {
          this.downloading = false;

          if (e.response && e.response.data) {
            if (e.response.data instanceof Blob) {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const errorData = JSON.parse(reader.result);
                  EventBus.$emit('show-alert', {
                    mensagem: errorData.mensagem || this.$t('equipment.qrcode.downloadError'),
                    tipo: 'error',
                  });
                } catch (parseError) {
                  EventBus.$emit('show-alert', {
                    mensagem: this.$t('equipment.qrcode.downloadError'),
                    tipo: 'error',
                  });
                }
              };
              reader.onerror = () => {
                EventBus.$emit('show-alert', {
                  mensagem: this.$t('equipment.qrcode.downloadError'),
                  tipo: 'error',
                });
              };
              reader.readAsText(e.response.data);
            } else {
              EventBus.$emit('show-alert', {
                mensagem: e.response.data.mensagem || this.$t('equipment.qrcode.downloadError'),
                tipo: 'error',
              });
            }
          } else {
            EventBus.$emit('show-error', { error: e });
          }
        },
      });
    },
  },
};
</script>
